"use client";

import React, { useEffect, useMemo, useState } from "react";

import { Check, Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useCollections } from "@/contexts/CollectionsContext";
import { useSavedProducts } from "@/contexts/SavedProductsContext";

interface SaveCollectionsDropdownProps {
  productId: string;
  allowRemove?: boolean;
  onClose?: () => void;
  className?: string;
  /** Hide the Done button (useful when rendered inside a sheet with its own footer) */
  hideDoneButton?: boolean;
  /** Called when the "new collection" input is shown/hidden - parent can pause auto-hide */
  onInputActiveChange?: (active: boolean) => void;
}

const SaveCollectionsDropdown: React.FC<SaveCollectionsDropdownProps> = ({
  productId,
  allowRemove = true,
  onClose,
  className = "",
  hideDoneButton = false,
  onInputActiveChange,
}) => {
  const {
    collections,
    addProductToCollection,
    removeProductFromCollection,
    createCollection,
    getProductCollections,
  } = useCollections();
  const { isSaved, toggleSaved } = useSavedProducts();

  const [loading, setLoading] = useState(false);
  const [loadingCollectionId, setLoadingCollectionId] = useState<string | null>(null);
  const [showNewCollectionInput, setShowNewCollectionInput] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [justUnsaved, setJustUnsaved] = useState(false);

  const initialMembership = useMemo(
    () => new Set(getProductCollections(productId)),
    [productId, getProductCollections]
  );
  const [membership, setMembership] = useState<Set<string>>(initialMembership);

  // Keep local membership in sync when collections change externally
  useEffect(() => {
    setMembership(new Set(getProductCollections(productId)));
  }, [collections, productId, getProductCollections]);

  // Reset justUnsaved when productId changes (new dropdown session)
  useEffect(() => {
    setJustUnsaved(false);
  }, [productId]);

  // Notify parent when new collection input is shown (to pause auto-hide timeout)
  useEffect(() => {
    onInputActiveChange?.(showNewCollectionInput);
  }, [showNewCollectionInput, onInputActiveChange]);

  const savedForUi = isSaved(productId);

  const sortedCollections = useMemo(() => {
    return [...collections]
      .filter((c) => !(c.isDefault || (c.name || "").trim().toLowerCase() === "all saves"))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [collections]);

  const handleCheckboxChange = async (collectionId: string, checked: boolean) => {
    if (loading) return;
    setLoading(true);
    setLoadingCollectionId(collectionId);
    try {
      if (checked) {
        const ok = await addProductToCollection(collectionId, productId);
        if (ok) {
          setMembership((prev) => new Set(prev).add(collectionId));
        }
      } else if (allowRemove) {
        const ok = await removeProductFromCollection(collectionId, productId);
        if (ok) {
          setMembership((prev) => {
            const next = new Set(prev);
            next.delete(collectionId);
            return next;
          });
        }
      }
    } finally {
      setLoading(false);
      setLoadingCollectionId(null);
    }
  };

  const handleCreateNewCollection = async () => {
    if (!newCollectionName.trim() || loading) return;
    setLoading(true);
    try {
      const created = await createCollection(newCollectionName.trim());
      if (created) {
        const ok = await addProductToCollection(created.id, productId);
        if (ok) {
          setMembership((prev) => new Set(prev).add(created.id));
        }
        setShowNewCollectionInput(false);
        setNewCollectionName("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`w-64 p-4 ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {savedForUi ? (
        <div className="mb-4 flex items-center justify-between gap-1.5 text-sm text-gray-900">
          <div className="flex items-center">
            <Check className="mr-2 h-4 w-4 text-black" />
            <span className="font-medium">Saved to Wishlist</span>
          </div>
          <button
            className="text-xs text-gray-600 underline hover:text-gray-900"
            onClick={async (e) => {
              e.stopPropagation();
              if (loading) return;
              setLoading(true);
              setJustUnsaved(true);
              try {
                await toggleSaved(productId);
              } catch {
                setJustUnsaved(false); // Revert on failure
              } finally {
                setLoading(false);
              }
            }}
          >
            Unsave
          </button>
        </div>
      ) : justUnsaved ? (
        <div className="mb-4 flex items-center justify-between gap-1.5 text-sm text-gray-500">
          <span className="font-medium">Removed from Wishlist</span>
          <button
            className="text-xs text-gray-600 underline hover:text-gray-900"
            onClick={async (e) => {
              e.stopPropagation();
              if (loading) return;
              setLoading(true);
              try {
                await toggleSaved(productId);
              } finally {
                setLoading(false);
              }
            }}
          >
            Save
          </button>
        </div>
      ) : (
        <div className="mb-4 flex items-center gap-1.5 text-sm text-gray-900">
          <span className="font-medium">Save to Wishlist</span>
        </div>
      )}

      <h3 className="mb-3 text-sm font-semibold text-gray-900">Save to a collection</h3>

      <div className="scrollbar-thin-y mb-3 max-h-60 space-y-1 overflow-y-auto overflow-x-hidden">
        {sortedCollections.map((collection) => {
          const isIn = membership.has(collection.id);
          const isLoadingThis = loadingCollectionId === collection.id;
          return (
            <div
              key={collection.id}
              className="flex min-h-[44px] cursor-pointer items-center rounded-lg px-1 py-2 transition-colors hover:bg-gray-50 active:bg-gray-100 sm:min-h-0 sm:py-1.5"
              onClick={() => !loading && handleCheckboxChange(collection.id, !isIn)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  // Only handle if the event originated from this div, not bubbled from checkbox
                  if (e.target !== e.currentTarget) return;
                  e.preventDefault();
                  if (!loading) handleCheckboxChange(collection.id, !isIn);
                }
              }}
            >
              <Checkbox
                id={`collection-${collection.id}-${productId}`}
                checked={isIn}
                onCheckedChange={(checked) => handleCheckboxChange(collection.id, Boolean(checked))}
                disabled={loading}
                className="mr-3 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              />
              <span
                className="flex-1 truncate text-sm font-normal text-gray-700"
                title={collection.name}
              >
                {collection.name}
              </span>
              {isLoadingThis && (
                <Loader2 className="ml-2 h-3.5 w-3.5 flex-shrink-0 animate-spin text-gray-400" />
              )}
            </div>
          );
        })}
      </div>

      {!showNewCollectionInput ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowNewCollectionInput(true)}
          className="h-auto w-full justify-start px-0 py-1 font-normal text-gray-500 hover:text-gray-700 has-[>svg]:px-0"
          disabled={loading}
        >
          <Plus className="mr-2 h-4 w-4" />
          <span>Create new collection</span>
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            placeholder="Collection name"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateNewCollection();
              }
              if (e.key === "Escape") {
                e.stopPropagation(); // Prevent dropdown from closing
                setShowNewCollectionInput(false);
                setNewCollectionName("");
              }
            }}
            className="h-8 text-xs"
            autoFocus
            disabled={loading}
          />
          <Button
            size="sm"
            onClick={handleCreateNewCollection}
            className="h-8 px-3 text-xs font-medium"
            disabled={loading || !newCollectionName.trim()}
          >
            Add
          </Button>
        </div>
      )}

      {/* Done button */}
      {!hideDoneButton && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-9 w-full text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
};

export default SaveCollectionsDropdown;
